import { useEffect, useMemo, useState } from 'react';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { IT_SERVICE_MANAGEMENT_COMPANY_MANAGED_KEY } from '@atlassian/jira-common-constants/src/project-template-type-keys.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';

import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { SERVICE_DESK } from '@atlassian/jira-shared-types/src/application-key.tsx';
import { useResource } from '@atlassian/react-resource-router/src/resources/controllers/use-resource/index.tsx';
import { firstProjectResource } from '@atlassian/jira-router-resources-service-desk-project-getting-started/src/services/index.tsx';
import {
	useFirstProjectDetails,
	useFirstProjectDetailsNew,
} from '../first-project-state/useIsFirstProjectState.tsx';

// Can be removed when cleaning up FG fix_useisfirstprojectitsm_hook
const useIsFirstProjectITSMOld = (inputProjectId?: string) => {
	const { data: containerContext, loading } = useProjectContext();
	const currentProjectId = inputProjectId || containerContext?.projectId;
	const [isFirstProject, setIsFirstProject] = useState(false);
	const [isFirstProjectItsm, setIsFirstProjectItsm] = useState(false);
	const [isStateLoading, setStateLoading] = useState(true);
	const { error: isFirstProjectError, firstProject } = useFirstProjectDetails();

	useEffect(() => {
		if (currentProjectId && !isFirstProjectError && firstProject) {
			setIsFirstProject(firstProject.projectId === `${currentProjectId}`);
			setIsFirstProjectItsm(
				firstProject.projectTemplateKey === IT_SERVICE_MANAGEMENT_COMPANY_MANAGED_KEY,
			);
			setStateLoading(false);
		} else if (isFirstProjectError) {
			setStateLoading(false);
		}
	}, [currentProjectId, firstProject, isFirstProjectError, setStateLoading]);

	const memoizedValue = useMemo(
		() => ({
			isLoading: loading || isStateLoading,
			isFirstProject,
			isFirstProjectItsm,
			isFirstProjectError,
		}),
		[isFirstProject, isFirstProjectItsm, isFirstProjectError, loading, isStateLoading],
	);
	return memoizedValue;
};

// Can be removed when cleaning up FG fix_useisfirstprojectitsm_hook
const useIsFirstProjectITSMNew = (inputProjectId?: string) => {
	const { data: containerContext, loading } = useProjectContext();
	const currentProjectId = inputProjectId || containerContext?.projectId;
	const {
		data: { error: isFirstProjectError, firstProject },
		loading: isFirstProjectDetailsLoading,
	} = useFirstProjectDetailsNew();

	const memoizedValue = useMemo(
		() => ({
			isLoading: loading || isFirstProjectDetailsLoading,
			isFirstProject: firstProject?.projectId === `${currentProjectId}`,
			isFirstProjectItsm:
				firstProject?.projectTemplateKey === IT_SERVICE_MANAGEMENT_COMPANY_MANAGED_KEY,
			isFirstProjectError,
		}),
		[loading, firstProject, isFirstProjectDetailsLoading, isFirstProjectError, currentProjectId],
	);
	return memoizedValue;
};

// Can be removed when cleaning up FG fix_useisfirstprojectitsm_hook
const useIsFirstProjectITSMConditional = (inputProjectId?: string) => {
	return getWillShowNav4()
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useIsFirstProjectITSMNew(inputProjectId)
		: // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useIsFirstProjectITSMOld(inputProjectId);
};

const useIsFirstProjectITSMCombined = (inputProjectId?: string) => {
	const { data: projectContext, loading: projectContextLoading } = useProjectContext();
	const currentProjectId = inputProjectId || projectContext?.projectId;

	const {
		loading: resourceLoading,
		data: resourceData,
		error: resourceError,
		refresh: getFirstProject,
	} = useResource(firstProjectResource);

	// We consider the resource to be loading if there is no data or error (initial state) or the resource is currently fetching
	const isResourceLoading = resourceLoading || (!resourceData && !resourceError);

	const licensedProducts = useLicensedProducts();
	const hasJsmLicense = licensedProducts[SERVICE_DESK];

	useEffect(() => {
		if (!hasJsmLicense || resourceData || resourceError) {
			return;
		}
		// We need to manually call fetch if the firstProjectResource is not fetched on the server side in
		// src/packages/router-resources/service-desk-common/src/index.tsx
		getFirstProject();
	}, [hasJsmLicense, resourceData, resourceError, getFirstProject]);

	return useMemo(
		() =>
			hasJsmLicense
				? {
						isLoading: projectContextLoading || isResourceLoading,
						isFirstProject: resourceData?.projectId === `${currentProjectId}`,
						isFirstProjectItsm:
							resourceData?.projectTemplateKey === IT_SERVICE_MANAGEMENT_COMPANY_MANAGED_KEY,
						isFirstProjectError: !!resourceError,
					}
				: {
						isLoading: false,
						isFirstProject: false,
						isFirstProjectItsm: false,
						isFirstProjectError: false,
					},
		[
			currentProjectId,
			hasJsmLicense,
			isResourceLoading,
			projectContextLoading,
			resourceData?.projectId,
			resourceData?.projectTemplateKey,
			resourceError,
		],
	);
};

export const useIsFirstProjectITSM = functionWithCondition(
	() => fg('fix_useisfirstprojectitsm_hook'),
	useIsFirstProjectITSMCombined,
	useIsFirstProjectITSMConditional,
);
